import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { 
  ArrowLeft, BarChart3, TrendingUp, TrendingDown, Eye, MousePointer, 
  ShoppingCart, Package, Megaphone, DollarSign, Calendar, Sparkles,
  ToggleLeft, ToggleRight, AlertTriangle, RefreshCw, Target, Award
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { toast } from 'sonner';
import {
  LineChart, Line, AreaChart, Area, BarChart, Bar, XAxis, YAxis, 
  CartesianGrid, Tooltip, ResponsiveContainer, PieChart, Pie, Cell
} from 'recharts';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const COLORS = ['#f59e0b', '#10b981', '#3b82f6', '#8b5cf6', '#ef4444'];

export default function BusinessAnalyticsPage() {
  const navigate = useNavigate();
  const [analytics, setAnalytics] = useState(null);
  const [subscription, setSubscription] = useState(null);
  const [demoEnabled, setDemoEnabled] = useState(false);
  const [loading, setLoading] = useState(true);
  const [togglingDemo, setTogglingDemo] = useState(false);

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    const token = localStorage.getItem('token');
    if (!token) {
      navigate('/business/register');
      return;
    }

    try {
      const headers = { Authorization: `Bearer ${token}` };

      // Fetch subscription first
      const subRes = await fetch(`${API_URL}/api/business/subscription`, { headers });
      if (subRes.ok) {
        const subData = await subRes.json();
        setSubscription(subData);
        
        // Check if user has analytics access
        if (!subData.plan_details?.features?.analytics_basic) {
          setLoading(false);
          return;
        }
      }

      // Fetch demo status
      const demoRes = await fetch(`${API_URL}/api/business/demo-data/status`, { headers });
      if (demoRes.ok) {
        const demoData = await demoRes.json();
        setDemoEnabled(demoData.enabled);
      }

      // Fetch detailed analytics
      const analyticsRes = await fetch(`${API_URL}/api/business/analytics/detailed`, { headers });
      if (analyticsRes.ok) {
        setAnalytics(await analyticsRes.json());
      }
    } catch (error) {
      console.error(error);
      toast.error('Erreur lors du chargement des statistiques');
    } finally {
      setLoading(false);
    }
  };

  const toggleDemoData = async () => {
    setTogglingDemo(true);
    const token = localStorage.getItem('token');
    
    try {
      const endpoint = demoEnabled 
        ? `${API_URL}/api/business/demo-data/disable`
        : `${API_URL}/api/business/demo-data/enable`;
      
      const res = await fetch(endpoint, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}` }
      });

      if (res.ok) {
        const data = await res.json();
        setDemoEnabled(data.enabled);
        toast.success(data.message);
        // Refresh analytics
        await fetchData();
      } else {
        throw new Error('Erreur');
      }
    } catch (error) {
      toast.error('Erreur lors de la modification des données de démo');
    } finally {
      setTogglingDemo(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="animate-spin w-8 h-8 border-4 border-primary border-t-transparent rounded-full" />
      </div>
    );
  }

  // Check subscription access
  if (!subscription?.plan_details?.features?.analytics_basic) {
    return (
      <div className="min-h-screen bg-gray-50">
        <div className="bg-white border-b">
          <div className="max-w-6xl mx-auto px-4 py-4 flex items-center gap-4">
            <Button variant="ghost" size="icon" asChild>
              <Link to="/business/dashboard">
                <ArrowLeft className="w-5 h-5" />
              </Link>
            </Button>
            <h1 className="font-semibold text-charcoal flex items-center gap-2">
              <BarChart3 className="w-5 h-5" /> Statistiques
            </h1>
          </div>
        </div>
        <div className="max-w-2xl mx-auto px-4 py-16 text-center">
          <div className="w-20 h-20 bg-gradient-to-r from-purple-500 to-pink-500 rounded-full flex items-center justify-center mx-auto mb-6">
            <BarChart3 className="w-10 h-10 text-white" />
          </div>
          <h2 className="text-2xl font-bold text-charcoal mb-4">
            Accédez aux statistiques avancées
          </h2>
          <p className="text-muted-foreground mb-8">
            Les statistiques et analyses de marché sont disponibles à partir du forfait Pro.
            Suivez vos performances, analysez vos campagnes et comparez vos prix au marché.
          </p>
          <Button asChild size="lg">
            <Link to="/business/subscription">
              <Sparkles className="w-5 h-5 mr-2" />
              Passer au forfait Pro
            </Link>
          </Button>
        </div>
      </div>
    );
  }

  const isPremium = subscription?.plan_details?.features?.analytics_advanced;

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <div className="bg-white border-b sticky top-0 z-10">
        <div className="max-w-7xl mx-auto px-4 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <Button variant="ghost" size="icon" asChild>
                <Link to="/business/dashboard">
                  <ArrowLeft className="w-5 h-5" />
                </Link>
              </Button>
              <h1 className="font-semibold text-charcoal flex items-center gap-2">
                <BarChart3 className="w-5 h-5" />
                Statistiques
              </h1>
            </div>
            
            {/* Demo Data Toggle */}
            <div className="flex items-center gap-4">
              <div className="flex items-center gap-2 text-sm">
                <span className="text-muted-foreground">Données de démo</span>
                <button
                  onClick={toggleDemoData}
                  disabled={togglingDemo}
                  className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${
                    demoEnabled ? 'bg-amber-500' : 'bg-gray-300'
                  }`}
                >
                  <span
                    className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                      demoEnabled ? 'translate-x-6' : 'translate-x-1'
                    }`}
                  />
                </button>
              </div>
              <Button variant="outline" size="sm" onClick={fetchData}>
                <RefreshCw className="w-4 h-4 mr-1" />
                Actualiser
              </Button>
            </div>
          </div>
        </div>
      </div>

      {/* Demo Banner */}
      {demoEnabled && (
        <div className="bg-amber-50 border-b border-amber-200">
          <div className="max-w-7xl mx-auto px-4 py-3 flex items-center justify-between">
            <div className="flex items-center gap-2 text-amber-800">
              <AlertTriangle className="w-4 h-4" />
              <span className="text-sm font-medium">
                Mode démo activé - Les données affichées sont simulées pour démonstration
              </span>
            </div>
            <Button 
              variant="ghost" 
              size="sm" 
              onClick={toggleDemoData}
              className="text-amber-700 hover:text-amber-900"
            >
              Désactiver
            </Button>
          </div>
        </div>
      )}

      <div className="max-w-7xl mx-auto px-4 py-6">
        {/* Overview Cards */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-8">
          <div className="bg-white rounded-xl p-4 shadow-sm">
            <div className="flex items-center gap-2 text-muted-foreground mb-1">
              <Eye className="w-4 h-4" />
              <span className="text-sm">Vues totales</span>
            </div>
            <p className="text-2xl font-bold text-charcoal">
              {analytics?.overview?.total_views?.toLocaleString() || 0}
            </p>
          </div>
          <div className="bg-white rounded-xl p-4 shadow-sm">
            <div className="flex items-center gap-2 text-muted-foreground mb-1">
              <MousePointer className="w-4 h-4" />
              <span className="text-sm">Clics totaux</span>
            </div>
            <p className="text-2xl font-bold text-charcoal">
              {analytics?.overview?.total_clicks?.toLocaleString() || 0}
            </p>
          </div>
          <div className="bg-white rounded-xl p-4 shadow-sm">
            <div className="flex items-center gap-2 text-muted-foreground mb-1">
              <ShoppingCart className="w-4 h-4" />
              <span className="text-sm">Conversions</span>
            </div>
            <p className="text-2xl font-bold text-charcoal">
              {analytics?.overview?.total_conversions?.toLocaleString() || 0}
            </p>
          </div>
          <div className="bg-white rounded-xl p-4 shadow-sm">
            <div className="flex items-center gap-2 text-muted-foreground mb-1">
              <TrendingUp className="w-4 h-4" />
              <span className="text-sm">Taux de clic</span>
            </div>
            <p className="text-2xl font-bold text-primary">
              {analytics?.overview?.click_rate || 0}%
            </p>
          </div>
        </div>

        {/* Charts Row */}
        <div className="grid lg:grid-cols-2 gap-6 mb-8">
          {/* Daily Timeline Chart */}
          <div className="bg-white rounded-xl p-6 shadow-sm">
            <h3 className="font-semibold text-charcoal mb-4 flex items-center gap-2">
              <Calendar className="w-5 h-5" />
              Évolution sur 30 jours
            </h3>
            {analytics?.daily_timeline?.length > 0 ? (
              <ResponsiveContainer width="100%" height={250}>
                <AreaChart data={analytics.daily_timeline}>
                  <defs>
                    <linearGradient id="colorViews" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="5%" stopColor="#f59e0b" stopOpacity={0.3}/>
                      <stop offset="95%" stopColor="#f59e0b" stopOpacity={0}/>
                    </linearGradient>
                  </defs>
                  <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                  <XAxis 
                    dataKey="date" 
                    tick={{ fontSize: 10 }}
                    tickFormatter={(val) => new Date(val).toLocaleDateString('fr-FR', { day: '2-digit', month: '2-digit' })}
                  />
                  <YAxis tick={{ fontSize: 10 }} />
                  <Tooltip 
                    labelFormatter={(val) => new Date(val).toLocaleDateString('fr-FR')}
                    formatter={(val, name) => [val, name === 'views' ? 'Vues' : name === 'clicks' ? 'Clics' : 'Conversions']}
                  />
                  <Area type="monotone" dataKey="views" stroke="#f59e0b" fill="url(#colorViews)" />
                  <Line type="monotone" dataKey="clicks" stroke="#10b981" strokeWidth={2} dot={false} />
                </AreaChart>
              </ResponsiveContainer>
            ) : (
              <div className="h-[250px] flex items-center justify-center text-muted-foreground">
                <p>Aucune donnée disponible. Activez les données de démo pour voir un exemple.</p>
              </div>
            )}
          </div>

          {/* Top Campaigns */}
          <div className="bg-white rounded-xl p-6 shadow-sm">
            <h3 className="font-semibold text-charcoal mb-4 flex items-center gap-2">
              <Award className="w-5 h-5" />
              Top campagnes
            </h3>
            {analytics?.top_campaigns?.length > 0 ? (
              <div className="space-y-3">
                {analytics.top_campaigns.map((campaign, index) => (
                  <div key={campaign.id} className="flex items-center gap-3 p-3 bg-gray-50 rounded-lg">
                    <span className={`w-8 h-8 rounded-full flex items-center justify-center text-white font-bold text-sm ${
                      index === 0 ? 'bg-amber-500' : index === 1 ? 'bg-gray-400' : 'bg-amber-700'
                    }`}>
                      {index + 1}
                    </span>
                    <div className="flex-1 min-w-0">
                      <p className="font-medium text-charcoal text-sm truncate">{campaign.title}</p>
                      <p className="text-xs text-muted-foreground">
                        {campaign.views} vues • {campaign.clicks} clics
                      </p>
                    </div>
                    <span className={`px-2 py-1 rounded text-xs font-medium ${
                      campaign.status === 'active' ? 'bg-green-100 text-green-700' :
                      campaign.status === 'completed' ? 'bg-blue-100 text-blue-700' :
                      'bg-gray-100 text-gray-700'
                    }`}>
                      {campaign.status === 'active' ? 'Active' : campaign.status === 'completed' ? 'Terminée' : campaign.status}
                    </span>
                  </div>
                ))}
              </div>
            ) : (
              <div className="h-[200px] flex items-center justify-center text-muted-foreground">
                <p>Aucune campagne. Créez votre première campagne pour voir les statistiques.</p>
              </div>
            )}
          </div>
        </div>

        {/* Premium Section: Price Comparison */}
        {isPremium && analytics?.price_comparisons?.length > 0 && (
          <div className="bg-white rounded-xl p-6 shadow-sm mb-8">
            <h3 className="font-semibold text-charcoal mb-4 flex items-center gap-2">
              <Target className="w-5 h-5" />
              Positionnement prix (vs marché)
              <span className="text-xs bg-purple-100 text-purple-700 px-2 py-1 rounded-full ml-2">Premium</span>
            </h3>
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead>
                  <tr className="border-b">
                    <th className="text-left py-2 px-3 text-sm font-medium text-muted-foreground">Produit</th>
                    <th className="text-right py-2 px-3 text-sm font-medium text-muted-foreground">Votre prix</th>
                    <th className="text-right py-2 px-3 text-sm font-medium text-muted-foreground">Moy. marché</th>
                    <th className="text-right py-2 px-3 text-sm font-medium text-muted-foreground">Min - Max</th>
                    <th className="text-right py-2 px-3 text-sm font-medium text-muted-foreground">Écart</th>
                    <th className="text-center py-2 px-3 text-sm font-medium text-muted-foreground">Position</th>
                  </tr>
                </thead>
                <tbody>
                  {analytics.price_comparisons.map((item, index) => (
                    <tr key={index} className="border-b last:border-0">
                      <td className="py-3 px-3 font-medium text-charcoal">{item.product}</td>
                      <td className="py-3 px-3 text-right font-semibold">{item.your_price?.toFixed(2)}€</td>
                      <td className="py-3 px-3 text-right text-muted-foreground">{item.market_avg?.toFixed(2)}€</td>
                      <td className="py-3 px-3 text-right text-xs text-muted-foreground">
                        {item.market_min?.toFixed(2)}€ - {item.market_max?.toFixed(2)}€
                      </td>
                      <td className={`py-3 px-3 text-right font-medium ${
                        item.diff_percent <= 0 ? 'text-green-600' : 'text-amber-600'
                      }`}>
                        {item.diff_percent > 0 ? '+' : ''}{item.diff_percent}%
                      </td>
                      <td className="py-3 px-3 text-center">
                        <span className={`inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs font-medium ${
                          item.position === 'competitive' 
                            ? 'bg-green-100 text-green-700' 
                            : 'bg-amber-100 text-amber-700'
                        }`}>
                          {item.position === 'competitive' ? (
                            <><TrendingDown className="w-3 h-3" /> Compétitif</>
                          ) : (
                            <><TrendingUp className="w-3 h-3" /> Premium</>
                          )}
                        </span>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            <p className="text-xs text-muted-foreground mt-4">
              Basé sur {analytics.price_comparisons.reduce((sum, p) => sum + (p.sample_count || 0), 0)} relevés de prix crowdsourcés
            </p>
          </div>
        )}

        {/* Premium Upsell for non-premium users */}
        {!isPremium && (
          <div className="bg-gradient-to-r from-purple-50 to-pink-50 rounded-xl p-6 border border-purple-200">
            <div className="flex items-start gap-4">
              <div className="w-12 h-12 bg-gradient-to-r from-purple-500 to-pink-500 rounded-xl flex items-center justify-center flex-shrink-0">
                <Target className="w-6 h-6 text-white" />
              </div>
              <div className="flex-1">
                <h3 className="font-semibold text-charcoal mb-1">Passez au Premium</h3>
                <p className="text-sm text-muted-foreground mb-4">
                  Accédez aux analyses avancées : positionnement prix vs marché, tendances, 
                  insights personnalisés et base de données de prix complète.
                </p>
                <Button asChild>
                  <Link to="/business/subscription">
                    <Sparkles className="w-4 h-4 mr-2" />
                    Découvrir Premium
                  </Link>
                </Button>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
